import React, { useEffect, useState } from "react";
import {
    Typography,
    Button,
    Box,
    Paper,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
    DragDropContext,
    Droppable,
    Draggable,
    DropResult,
} from "react-beautiful-dnd";

import { Loader } from "../../components/loader/Loader";
import {
    ChannelkartNetworkGet,
    META_DATA_BATCH_API,
} from "../../services/networkService";
import { updateConfigRequestBody } from "../../services/managePoliciesService";
import { metaDataBatchPayload } from "../../types/types";
import { getLob } from "../../services/authenticationService";
import { validateWithOtp } from "../../stateManagement/actions/validateOtpPopupActions";
import { manageUpdateAccessObj } from "../manageUpdateAccess/manageUpdateAccessTypes";
import { getMetaDataConfig, validateMetaDataResponse } from "../../services/uiConfigService";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { getConfigFromClientConfig } from "../../services/bannerServices";

type CategoryItem = {
    category: string;
    count: number;
};

const ProductSequencing = () => {
    const [loading, setLoading] = useState(true);
    const [categoryList, setCategoryList] = useState<CategoryItem[]>([]);
    const [existingDomainValues, setExistingDomainValues] = useState<any[]>([]);
    const [catalogueConfig, setCatalogueConfig] = useState<any[]>([]);
    const generate4DigitId = () => Math.floor(1000 + Math.random() * 9000).toString();

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                setLoading(true);
                let page = 0;
                const size = 5000;
                let allProducts: any[] = [];
                while (true) {
                    const response = await ChannelkartNetworkGet(
                        `/products?page=${page}&size=${size}`
                    );
                    const products = response?.data?.features || [];
                    if (!products.length) break;
                    allProducts = [...allProducts, ...products];
                    page++;
                }
                const categoryCountMap: Record<string, number> = {};
                allProducts.forEach((item) => {
                    const category = item.category;
                    categoryCountMap[category] = (categoryCountMap[category] || 0) + 1;
                });
                const groupedCategories: CategoryItem[] = Object.keys(categoryCountMap).map(
                    (key) => ({
                        category: key,
                        count: categoryCountMap[key],
                    })
                );
                const configName = "catalogue_configuration";
                const clientConfig = await getMetaDataConfig("clientconfig");
                const configResp = getConfigFromClientConfig(clientConfig, configName);
                setCatalogueConfig(configResp);
                const sortingRulesConfig = configResp?.find(
                    (option: { name: string }) =>
                        option.name === "catalogProductSortingRules"
                );
                const domainValues = Array.isArray(sortingRulesConfig?.value)
                    ? sortingRulesConfig.value
                    : [
                        {
                            priority: [],
                            sortOrder: [],
                        },
                    ];

                setExistingDomainValues(domainValues);
                const categoryPriority: string[] =
                    domainValues?.[0]?.priority?.find(
                        (p: any) => p.attribute === "category"
                    )?.values || [];
                const categoryMap = new Map(
                    groupedCategories.map(item => [item.category, item])
                );
                const orderedFromConfig: CategoryItem[] = categoryPriority
                    .filter(category => categoryMap.has(category))
                    .map(category => categoryMap.get(category)!);
                const remainingCategories: CategoryItem[] = groupedCategories
                    .filter(item => !categoryPriority.includes(item.category))
                    .sort((a, b) => a.category.localeCompare(b.category));
                setCategoryList([
                    ...orderedFromConfig,
                    ...remainingCategories,
                ]);
            } catch (error) {
                console.error(error);
                openPopup("Error", "Failed to load products");
            } finally {
                setLoading(false);
            }
        };
        fetchAllData();
    }, []);

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const items = Array.from(categoryList);
        const [movedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, movedItem);
        setCategoryList(items);
    };

    const handleSave = async () => {
        try {
            const updatedCategoryValues = categoryList.map(
                (item) => item.category
            );
            const baseConfig = existingDomainValues[0] || {
                priority: [],
                sortOrder: [],
            };
            const updatedPriority = [...(baseConfig.priority || [])];
            const categoryIndex = updatedPriority.findIndex(
                (item: any) => item.attribute === "category"
            );
            if (categoryIndex > -1) {
                updatedPriority[categoryIndex] = {
                    ...updatedPriority[categoryIndex],
                    id: updatedPriority[categoryIndex].id || generate4DigitId(),
                    values: updatedCategoryValues,
                };
            } else {
                updatedPriority.push({
                    id: generate4DigitId(),
                    attribute: "category",
                    values: updatedCategoryValues,
                });
            }
            const updatedDomainValues = [
                {
                    ...baseConfig,
                    priority: updatedPriority,
                },
            ];
            const updatedCatalogueConfig = [...catalogueConfig];
            const sortingRuleIndex = updatedCatalogueConfig.findIndex(
                (item: any) => item.name === "catalogProductSortingRules"
            );

            if (sortingRuleIndex > -1) {
                updatedCatalogueConfig[sortingRuleIndex] = {
                    ...updatedCatalogueConfig[sortingRuleIndex],
                    value: updatedDomainValues,
                };
            } else {
                updatedCatalogueConfig.push({
                    name: "catalogProductSortingRules",
                    type: "object",
                    value: updatedDomainValues,
                    docUrl: "",
                    description: "Catalog product sorting rules",
                });
            }
            const ProductSequencingPayload: updateConfigRequestBody = {
                domainName: "clientconfig",
                domainType: "catalogue_configuration",
                domainValues: updatedCatalogueConfig,
                lob: getLob(),
            };
            const finalMetaDataPayload: metaDataBatchPayload = {
                features: [ProductSequencingPayload],
            };
            validateWithOtp(
                (verifiedUser?: manageUpdateAccessObj, verifyResponse?: any) => {
                    try {
                        if (verifyResponse.status >= 200 && verifyResponse.status < 300) {
                            const validationObj = validateMetaDataResponse(
                                verifyResponse?.data?.features
                            );
                            if (validationObj.success) {
                                openPopup("Success", "Config Updated Successfully");
                            } else {
                                openPopup("Error", validationObj.message);
                            }
                        } else {
                            throw new Error();
                        }
                    } catch (error) {
                        openPopup("Error", "Something went wrong");
                    }
                },
                undefined,
                "all",
                META_DATA_BATCH_API,
                finalMetaDataPayload,
                "PUT"
            );
        } catch (error) {
            openPopup("Error", "Something went wrong");
            setLoading(false);
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="config-container-left" style={{ marginTop: "0", padding: "30px" }}>
            <div className="config-heading" style={{ height: "auto", width: "100%" }}>
                <div>
                    <Typography
                        sx={{
                            fontSize: { xs: "1rem", md: "1.2rem", xl: "2.8rem" },
                            color: "#227C9D",
                        }}
                    >
                        <span style={{ color: "#00C6B1", fontWeight: "bold" }}>
                            PRODUCT DIVISION
                        </span>{" "}
                        CATALOG ORDER
                    </Typography>
                    <Typography style={{ fontSize: "0.9rem" }}>
                        Reorder product divisions to control how they appear in the SFA and
                        Supervisor catalog.
                    </Typography>
                </div>
            </div>

            <div className="config-main-logic-container">
                <div className="config-main" style={{ padding: "20px" }}>
                    <Typography sx={{ mb: 1, fontSize: "1.2rem", fontWeight: 600 }}>
                        Division Sequence
                    </Typography>
                    <Typography sx={{ mb: 2, color: "#6b7280", fontSize: "0.9rem" }}>
                        Drag divisions to reorder. The first division will appear at the
                        top of the catalog.
                    </Typography>

                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="categories">
                            {(provided) => (
                                <Box
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {categoryList.map((item, index) => (
                                        <Draggable
                                            key={item.category}
                                            draggableId={item.category}
                                            index={index}
                                        >
                                            {(provided) => (
                                                <Paper
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        padding: "16px",
                                                        marginBottom: "12px",
                                                        borderRadius: "12px",
                                                        border: "1px solid #e5e7eb",
                                                        boxShadow: "none",
                                                    }}
                                                >
                                                    <Box
                                                        {...provided.dragHandleProps}
                                                        sx={{
                                                            marginRight: "16px",
                                                            color: "#9ca3af",
                                                            cursor: "grab",
                                                            display: "flex",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <DragIndicatorIcon />
                                                    </Box>

                                                    <Box>
                                                        <Typography sx={{ fontWeight: 600 }}>
                                                            {item.category}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontSize: "0.85rem",
                                                                color: "#6b7280",
                                                            }}
                                                        >
                                                            {item.count} products
                                                        </Typography>
                                                    </Box>
                                                </Paper>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </Box>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>

                <div className="config-save-button-container">
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        sx={{
                            alignSelf: "flex-end",
                            marginY: "1rem",
                            fontSize: "clamp(0.5rem,0.9vw + 0.065rem, 1.6rem)",
                            gap: "7px",
                        }}
                    >
                        SAVE
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ProductSequencing;
